/** A conversão para o mapa (IfcMapConversion), lida como a norma manda,
 *  com cada valor a dizer de que entidade veio.
 *
 *  O que a norma diz (IFC4 e IFC4.3, IfcMapConversion e IfcProjectedCRS):
 *  - primeiro escala-se por Scale, depois roda-se, e só depois se somam
 *    Eastings, Northings e OrthogonalHeight. A translação está, portanto,
 *    nas unidades do MAPA;
 *  - as unidades do mapa são o MapUnit do IfcProjectedCRS. Se o MapUnit
 *    não vier, "the unit for the coordinate axes is taken from the
 *    default units, as stated in IfcProject.UnitInContext": a unidade de
 *    comprimento do projecto;
 *  - Scale converte as unidades do projecto nas do mapa. Se não vier,
 *    vale 1,0.
 *
 *  O viewer trabalha em metros (o Fragments converte a geometria). Daí:
 *    escala geométrica = Scale x (metros por unidade do mapa)
 *                              / (metros por unidade do projecto)
 *    origem em metros  = (E, N, H) x (metros por unidade do mapa)
 *
 *  Antes assumia-se o mapa sempre em metros. Num ficheiro em milímetros
 *  sem MapUnit e com Scale omitido (o que a norma permite e quer dizer
 *  "o mapa também está em mm"), o viewer punha o modelo 1000 vezes maior
 *  e a origem 1000 vezes mais longe.
 *
 *  Nada se escolhe: duas unidades de comprimento no IfcUnitAssignment
 *  são ambíguas e a leitura não aplica nada; o IfcMapConversionScaled
 *  com factores diferentes nos três eixos não se representa numa escala
 *  única e diz-se isso.
 *
 *  Fontes, por esta ordem (a primeira que existe):
 *  1. IfcMapConversion / IfcMapConversionScaled + IfcProjectedCRS (IFC4,
 *     IFC4.3);
 *  2. os property sets ePset_MapConversion + ePset_ProjectedCRS do guia
 *     de georreferenciação da buildingSMART para IFC2X3 (o nome compara-se
 *     sem maiúsculas: há quem escreva ePSet_ e quem escreva ePset_). Os
 *     comprimentos são IfcLengthMeasure: valem na unidade que a própria
 *     propriedade declarar, senão na do projecto; o MapUnit do
 *     ePset_ProjectedCRS, quando existe, também se confere;
 *  3. sem nenhuma das duas, o IfcSite (RefLatitude, RefLongitude,
 *     RefElevation): ver lerLocalizacaoDoSitio. */

import { findEntityById, findEntityByType, splitTopLevelArgs } from '../stepText'
import { lerArgTexto, lerListaDeRefs, lerRef, lerValorTipado } from '../step/textoStep'

export interface UnidadeLida {
  /** Metros por unidade. */
  metros: number
  simbolo: string
  /** Entidade de onde veio (#id). */
  id: number
}

export type FonteDoMapa = 'IFCMAPCONVERSION' | 'IFCMAPCONVERSIONSCALED' | 'EPSET_MAPCONVERSION'

export interface LeituraDoMapa {
  conversao: {
    id: number
    classe: FonteDoMapa
    eastings: number
    northings: number
    altura: number
    xAbscissa: number
    xOrdenada: number
    /** Como vem no ficheiro; null quando omitido (vale 1,0). */
    scale: number | null
    factores: [number, number, number] | null
  }
  crs: { id: number; nome: string | null } | null
  unidadeDoProjecto: UnidadeLida | null
  /** A unidade do mapa e de onde veio: o MapUnit, ou a do projecto quando
   *  o MapUnit é omitido. */
  unidadeDoMapa: (UnidadeLida & { origem: OrigemDaUnidade }) | null
  /** null quando falta saber uma das unidades (não se adivinha). */
  escalaGeometrica: number | null
  origemEmMetros: [number, number, number] | null
  rotacaoGraus: number
  avisos: string[]
  /** Quando o ficheiro se contradiz (a unidade do mapa que declara não é
   *  a do CRS que nomeia), as duas leituras possíveis. Nesse caso não se
   *  aplica nenhuma sozinha: escalaGeometrica e origemEmMetros vêm null. */
  alternativas: AlternativaDoMapa[]
  /** O que o índice EPSG sabe do CRS (preenchido por conferirCrs). */
  crsInfo?: import('@viewer/map/crsDefs').InfoDoCrs | null
}

/** De onde veio a unidade das coordenadas do mapa: o MapUnit (entidade
 *  ou, no IFC2X3, o rótulo do ePset_ProjectedCRS), a unidade declarada nas
 *  próprias propriedades do ePset_MapConversion, ou a do projecto. */
export type OrigemDaUnidade = 'MapUnit' | 'propriedade' | 'projecto'

export interface AlternativaDoMapa {
  rotulo: string
  /** A origem desta leitura cai fora da área de uso do CRS (conferirCrs). */
  foraDaArea?: boolean
  escalaGeometrica: number
  origemEmMetros: [number, number, number]
}

const PREFIXO: Record<string, number> = {
  EXA: 1e18, PETA: 1e15, TERA: 1e12, GIGA: 1e9, MEGA: 1e6, KILO: 1e3, HECTO: 1e2, DECA: 1e1,
  DECI: 1e-1, CENTI: 1e-2, MILLI: 1e-3, MICRO: 1e-6, NANO: 1e-9, PICO: 1e-12, FEMTO: 1e-15, ATTO: 1e-18,
}
const SIMBOLO_PREFIXO: Record<string, string> = { KILO: 'k', HECTO: 'h', DECA: 'da', DECI: 'd', CENTI: 'c', MILLI: 'm', MICRO: 'µ', NANO: 'n' }

function args(texto: string, id: number): { classe: string; a: string[] } | null {
  const e = findEntityById(texto, id)
  return e ? { classe: e.type, a: splitTopLevelArgs(e.args).map((x) => x.trim()) } : null
}

function idDe(texto: string, inicio: number): number {
  return parseInt(/^#(\d+)/.exec(texto.slice(inicio, inicio + 20))?.[1] ?? '0', 10)
}

const enumeracao = (s: string | undefined) => (s ?? '').replace(/^\.|\.$/g, '')

/** Metros por unidade de uma unidade de comprimento (SI ou por conversão). */
export function lerUnidadeDeComprimento(texto: string, id: number, fundo = 0): UnidadeLida | null {
  const e = args(texto, id)
  if (!e || fundo > 4) return null
  if (e.classe === 'IFCSIUNIT') {
    if (enumeracao(e.a[1]) !== 'LENGTHUNIT' || enumeracao(e.a[3]) !== 'METRE') return null
    const p = e.a[2] === '$' ? '' : enumeracao(e.a[2])
    const f = p === '' ? 1 : PREFIXO[p]
    if (f === undefined) return null
    return { metros: f, simbolo: `${SIMBOLO_PREFIXO[p] ?? ''}m`, id }
  }
  if (e.classe === 'IFCCONVERSIONBASEDUNIT' || e.classe === 'IFCCONVERSIONBASEDUNITWITHOFFSET') {
    if (enumeracao(e.a[1]) !== 'LENGTHUNIT') return null
    const nome = lerArgTexto(e.a[2]) ?? '?'
    const mwu = lerRef(e.a[3])
    const m = mwu !== null ? args(texto, mwu) : null
    if (!m) return null
    const v = lerValorTipado(m.a[0])
    const n = v ? Number(v.interior) : NaN
    const base = lerRef(m.a[1])
    const b = base !== null ? lerUnidadeDeComprimento(texto, base, fundo + 1) : null
    if (!Number.isFinite(n) || !b) return null
    return { metros: n * b.metros, simbolo: nome.toLowerCase(), id }
  }
  return null
}

/** A unidade de comprimento do projecto (IfcProject.UnitsInContext).
 *  Duas unidades de comprimento: ambígua, não se escolhe. */
export function lerUnidadeDoProjecto(texto: string): { unidade: UnidadeLida | null; aviso: string | null } {
  const proj = findEntityByType(texto, 'IFCPROJECT')
  let ua: number | null
  if (proj) {
    ua = lerRef(splitTopLevelArgs(proj.args).map((x) => x.trim())[8])
  } else {
    // Sem IfcProject: um único IfcUnitAssignment no ficheiro não deixa
    // nada por escolher; mais de um, sim.
    const todas = [...texto.matchAll(/#(\d+)\s*=\s*IFCUNITASSIGNMENT\s*\(/g)]
    if (todas.length !== 1) {
      return { unidade: null, aviso: todas.length === 0 ? 'O ficheiro não tem IfcProject nem IfcUnitAssignment.' : 'O ficheiro não tem IfcProject e tem mais de um IfcUnitAssignment: não se escolhe.' }
    }
    ua = parseInt(todas[0][1], 10)
  }
  const u = ua !== null ? args(texto, ua) : null
  if (!u) return { unidade: null, aviso: proj ? `O IfcProject (#${idDe(texto, proj.start)}) não declara unidades.` : `O IfcUnitAssignment não se lê.` }
  const comp = lerListaDeRefs(u.a[0])
    .map((id) => lerUnidadeDeComprimento(texto, id))
    .filter((x): x is UnidadeLida => x !== null)
  if (comp.length === 0) {
    return { unidade: null, aviso: `O IfcUnitAssignment (#${ua}) não declara unidade de comprimento.` }
  }
  if (comp.length > 1) {
    return {
      unidade: null,
      aviso: `O IfcUnitAssignment (#${ua}) declara ${comp.length} unidades de comprimento (${comp.map((c) => `${c.simbolo} #${c.id}`).join(', ')}): é ambíguo e não se escolhe nenhuma.`,
    }
  }
  return { unidade: comp[0], aviso: null }
}

const fmt = (n: number) => String(Number(n.toPrecision(6))).replace('.', ',')

/** A leitura de cada modelo, feita ao abrir, para o painel Informações
 *  do modelo a mostrar sem voltar a ler o ficheiro. */
const leituras = new Map<string, LeituraDoMapa | null>()
export function guardarLeitura(modelId: string, l: LeituraDoMapa | null): void {
  leituras.set(modelId, l)
}
export function leituraGuardada(modelId: string): LeituraDoMapa | null | undefined {
  return leituras.get(modelId)
}

/** O que uma fonte (entidade ou ePset) declara, antes de se interpretar
 *  as unidades. */
interface Declarado {
  id: number
  classe: FonteDoMapa
  eastings: number
  northings: number
  altura: number
  xAbscissa: number
  xOrdenada: number
  scale: number | null
  factores: [number, number, number] | null
  crs: { id: number; nome: string | null } | null
  /** Unidades que o ficheiro declara para as coordenadas do mapa, cada uma
   *  com a origem. Vazio: vale a do projecto. */
  unidades: (UnidadeLida & { origem: OrigemDaUnidade })[]
  avisos: string[]
}

const numero = (s: string | undefined): number | null => {
  const v = lerValorTipado(s)
  const n = Number(v ? v.interior : s)
  return s === undefined || s === '$' || s.trim() === '' || !Number.isFinite(n) ? null : n
}

function lerEntidade(texto: string): Declarado | null {
  const scaled = findEntityByType(texto, 'IFCMAPCONVERSIONSCALED')
  const ent = scaled ?? findEntityByType(texto, 'IFCMAPCONVERSION')
  if (!ent) return null
  const avisos: string[] = []
  const id = idDe(texto, ent.start)
  const a = splitTopLevelArgs(ent.args).map((x) => x.trim())
  let xAbscissa = numero(a[5])
  let xOrdenada = numero(a[6])
  if (xAbscissa === null || xOrdenada === null) {
    avisos.push(`XAxisAbscissa/XAxisOrdinate omitidos no #${id}: sem rotação.`)
    xAbscissa = 1
    xOrdenada = 0
  }
  const scale = numero(a[7])
  if (scale === null) avisos.push(`Scale omitido no #${id}: vale 1,0, como manda a norma.`)
  let factores: [number, number, number] | null = null
  if (scaled) {
    factores = [numero(a[8]) ?? 1, numero(a[9]) ?? 1, numero(a[10]) ?? 1]
    if (!(factores[0] === factores[1] && factores[1] === factores[2])) {
      avisos.push(
        `O IfcMapConversionScaled (#${id}) declara factores diferentes em X, Y e Z (${factores.map(fmt).join(', ')}). O viewer só representa uma escala igual nos três eixos: os factores não se aplicam, e a posição continua certa.`,
      )
    }
  }
  const crsId = lerRef(a[1])
  const crsArgs = crsId !== null ? args(texto, crsId) : null
  const crs = crsArgs ? { id: crsId!, nome: lerArgTexto(crsArgs.a[0]) } : null
  const unidades: Declarado['unidades'] = []
  const mapUnitRef = crsArgs?.classe === 'IFCPROJECTEDCRS' ? lerRef(crsArgs.a[6]) : null
  if (mapUnitRef !== null) {
    const u = lerUnidadeDeComprimento(texto, mapUnitRef)
    if (u) unidades.push({ ...u, origem: 'MapUnit' })
    else avisos.push(`O MapUnit (#${mapUnitRef}) do #${crsId} não é uma unidade de comprimento que se saiba ler.`)
  }
  return {
    id,
    classe: scaled ? 'IFCMAPCONVERSIONSCALED' : 'IFCMAPCONVERSION',
    eastings: numero(a[2]) ?? 0,
    northings: numero(a[3]) ?? 0,
    altura: numero(a[4]) ?? 0,
    xAbscissa,
    xOrdenada,
    scale,
    factores,
    crs,
    unidades,
    avisos,
  }
}

/** Os IfcPropertySet com este nome (sem distinguir maiúsculas), pela ordem do ficheiro. */
function psetsChamados(texto: string, nome: string): number[] {
  const re = new RegExp(`#(\\d+)\\s*=\\s*IFCPROPERTYSET\\s*\\(\\s*'[^']*'\\s*,[^,]*,\\s*'${nome}'`, 'gi')
  return [...texto.matchAll(re)].map((m) => parseInt(m[1], 10))
}

interface PropriedadeLida {
  id: number
  valor: string | undefined
  unidade: number | null
}

function propriedadesDoPset(texto: string, pset: number): Map<string, PropriedadeLida> {
  const out = new Map<string, PropriedadeLida>()
  const p = args(texto, pset)
  if (!p) return out
  for (const pid of lerListaDeRefs(p.a[4])) {
    const e = args(texto, pid)
    if (!e || e.classe !== 'IFCPROPERTYSINGLEVALUE') continue
    const nome = lerArgTexto(e.a[0])
    if (nome) out.set(nome.toLowerCase(), { id: pid, valor: e.a[2], unidade: lerRef(e.a[3]) })
  }
  return out
}

/** "METRE", "MILLIMETRE", "FOOT"... (o rótulo do ePset_ProjectedCRS). */
export function metrosDoRotulo(rotulo: string): { metros: number; simbolo: string } | null {
  const t = rotulo.trim().toUpperCase().replace(/[\s-]+/g, '_')
  if (t === 'M' || t === 'METRE' || t === 'METER' || t === 'METRES' || t === 'METERS') return { metros: 1, simbolo: 'm' }
  const pref = /^([A-Z]+?)(METRE|METER)S?$/.exec(t)
  if (pref && PREFIXO[pref[1]] !== undefined) return { metros: PREFIXO[pref[1]], simbolo: `${SIMBOLO_PREFIXO[pref[1]] ?? ''}m` }
  if (t === 'MM') return { metros: 1e-3, simbolo: 'mm' }
  if (t === 'US_SURVEY_FOOT' || t === 'US_SURVEY_FEET' || t === 'FTUS') return { metros: 1200 / 3937, simbolo: 'ft (US)' }
  if (t === 'FOOT' || t === 'FEET' || t === 'FT' || t === 'INTERNATIONAL_FOOT') return { metros: 0.3048, simbolo: 'ft' }
  return null
}

function lerEpset(texto: string): Declarado | null {
  const mcs = psetsChamados(texto, 'epset_mapconversion')
  if (mcs.length === 0) return null
  const avisos: string[] = []
  const id = mcs[0]
  if (mcs.length > 1) avisos.push(`Há ${mcs.length} ePset_MapConversion (${mcs.map((x) => `#${x}`).join(', ')}): usa-se o primeiro do ficheiro, #${id}.`)
  const mc = propriedadesDoPset(texto, id)
  const v = (nome: string) => numero(mc.get(nome.toLowerCase())?.valor)

  const unidades: Declarado['unidades'] = []
  // A unidade declarada nas propriedades de comprimento (Unit da
  // IfcPropertySingleValue).
  const unidadesDasProps = ['Eastings', 'Northings', 'OrthogonalHeight']
    .map((n) => mc.get(n.toLowerCase()))
    .filter((x): x is PropriedadeLida => !!x && x.unidade !== null)
    .map((x) => lerUnidadeDeComprimento(texto, x.unidade!))
  const lidas = unidadesDasProps.filter((x): x is UnidadeLida => x !== null)
  if (lidas.length > 0) {
    if (lidas.some((u) => Math.abs(u.metros / lidas[0].metros - 1) > 1e-9)) {
      avisos.push(`As propriedades do #${id} declaram unidades diferentes entre si (${lidas.map((u) => `${u.simbolo} #${u.id}`).join(', ')}): usa-se a de Eastings.`)
    }
    unidades.push({ ...lidas[0], origem: 'propriedade' })
  } else if (unidadesDasProps.length > 0) {
    avisos.push(`A unidade das propriedades do #${id} não é uma unidade de comprimento que se saiba ler.`)
  }

  // O CRS: ePset_ProjectedCRS.Name, senão o TargetCRS do próprio ePset.
  const crsIds = psetsChamados(texto, 'epset_projectedcrs')
  const crsProps = crsIds.length > 0 ? propriedadesDoPset(texto, crsIds[0]) : new Map<string, PropriedadeLida>()
  const nomeCrs = lerArgTexto(lerValorTipado(crsProps.get('name')?.valor)?.interior) ??
    lerArgTexto(lerValorTipado(mc.get('targetcrs')?.valor)?.interior)
  const crs = nomeCrs || crsIds.length > 0 ? { id: crsIds[0] ?? id, nome: nomeCrs } : null
  const mapUnit = crsProps.get('mapunit')
  const rotulo = mapUnit ? lerArgTexto(lerValorTipado(mapUnit.valor)?.interior) : null
  if (mapUnit && rotulo) {
    const u = metrosDoRotulo(rotulo)
    if (u) unidades.push({ ...u, id: mapUnit.id, origem: 'MapUnit' })
    else avisos.push(`O MapUnit "${rotulo}" (#${mapUnit.id}) não é uma unidade de comprimento que se saiba ler.`)
  }

  let xAbscissa = v('XAxisAbscissa')
  let xOrdenada = v('XAxisOrdinate')
  if (xAbscissa === null || xOrdenada === null) {
    avisos.push(`XAxisAbscissa/XAxisOrdinate em falta no #${id}: sem rotação.`)
    xAbscissa = 1
    xOrdenada = 0
  }
  const scale = v('Scale')
  if (scale === null) avisos.push(`Scale em falta no #${id}: vale 1,0.`)
  return {
    id,
    classe: 'EPSET_MAPCONVERSION',
    eastings: v('Eastings') ?? 0,
    northings: v('Northings') ?? 0,
    altura: v('OrthogonalHeight') ?? 0,
    xAbscissa,
    xOrdenada,
    scale,
    factores: null,
    crs,
    unidades,
    avisos,
  }
}

/** @param opcoes.metrosDoCrs  metros por unidade dos eixos do CRS que o
 *  ficheiro nomeia (do EPSG), quando se conhece. Serve para apanhar o
 *  ficheiro que declara uma unidade de mapa diferente da do próprio CRS. */
export function lerConversaoDoMapa(texto: string, opcoes?: { metrosDoCrs?: number | null }): LeituraDoMapa | null {
  const d = lerEntidade(texto) ?? lerEpset(texto)
  if (!d) return null
  const avisos = [...d.avisos]
  const { eastings, northings, altura, xAbscissa, xOrdenada, scale, factores, crs, id } = d
  const { unidade: unidadeDoProjecto, aviso: avisoProjecto } = lerUnidadeDoProjecto(texto)
  if (avisoProjecto) avisos.push(avisoProjecto)

  // As unidades declaradas para o mapa; sem nenhuma, a do projecto.
  const candidatas: { metros: number; rotulo: string }[] = []
  let unidadeDoMapa: LeituraDoMapa['unidadeDoMapa'] = d.unidades[0] ?? null
  for (const u of d.unidades) {
    candidatas.push({
      metros: u.metros,
      rotulo:
        u.origem === 'MapUnit'
          ? `Pelo MapUnit (${u.simbolo}, #${u.id})`
          : `Pela unidade das propriedades (${u.simbolo}, #${u.id})`,
    })
  }
  if (d.unidades.length === 0 && unidadeDoProjecto) {
    unidadeDoMapa = { ...unidadeDoProjecto, origem: 'projecto' }
    candidatas.push({
      metros: unidadeDoProjecto.metros,
      rotulo: `Pela norma, sem MapUnit: unidade do projecto (${unidadeDoProjecto.simbolo}, #${unidadeDoProjecto.id})`,
    })
    avisos.push(
      d.classe === 'EPSET_MAPCONVERSION'
        ? `O ePset_MapConversion (#${id}) não declara unidade: as coordenadas são IfcLengthMeasure e valem na unidade do projecto (${unidadeDoProjecto.simbolo}, #${unidadeDoProjecto.id}).`
        : `MapUnit omitido${crs ? ` no #${crs.id}` : ''}: as coordenadas do mapa estão na unidade do projecto (${unidadeDoProjecto.simbolo}, #${unidadeDoProjecto.id}), como manda a norma.`,
    )
  }
  const metrosDoCrs = opcoes?.metrosDoCrs ?? null
  if (metrosDoCrs !== null && candidatas.length > 0) {
    candidatas.push({
      metros: metrosDoCrs,
      rotulo: `Pela unidade dos eixos de ${crs?.nome ?? 'o CRS'} (${metrosDoCrs === 1 ? 'm' : `${fmt(metrosDoCrs)} m`})`,
    })
  }
  // Candidatas distintas (1% de tolerância).
  const distintas = candidatas.filter(
    (c, i) => candidatas.findIndex((o) => Math.abs(o.metros / c.metros - 1) <= 0.01) === i,
  )

  let escalaGeometrica: number | null = null
  let origemEmMetros: [number, number, number] | null = null
  const alternativas: AlternativaDoMapa[] = []
  const factorComum = factores && factores[0] === factores[1] && factores[1] === factores[2] ? factores[0] : 1
  const leituraCom = (metrosDoMapa: number, rotulo: string): AlternativaDoMapa => ({
    rotulo,
    escalaGeometrica: (((scale ?? 1) * metrosDoMapa) / unidadeDoProjecto!.metros) * factorComum,
    origemEmMetros: [eastings * metrosDoMapa, northings * metrosDoMapa, altura * metrosDoMapa],
  })
  if (!unidadeDoProjecto) {
    avisos.push('Sem a unidade do projecto não se sabe converter a posição: a conversão para o mapa não se aplica.')
  } else if (distintas.length > 1) {
    // O ficheiro diz duas coisas diferentes sobre a unidade do mapa. Não
    // se escolhe: mostram-se as leituras.
    alternativas.push(...distintas.map((c) => leituraCom(c.metros, c.rotulo)))
    avisos.push(
      `O ficheiro contradiz-se sobre a unidade das coordenadas do mapa (${distintas.map((c) => c.rotulo.toLowerCase()).join('; ')}). As leituras dão posições e escalas diferentes; não se aplica nenhuma sem escolha.`,
    )
  } else if (distintas.length === 1) {
    ;({ escalaGeometrica, origemEmMetros } = leituraCom(distintas[0].metros, ''))
    const metrosDoMapa = distintas[0].metros
    if (Math.abs(escalaGeometrica - 1) > 0.01) {
      avisos.push(
        `Com o que o ficheiro declara (Scale ${scale === null ? 'omitido = 1' : fmt(scale)}, projecto em ${unidadeDoProjecto.simbolo}, mapa em ${unidadeDoMapa?.simbolo ?? `${fmt(metrosDoMapa)} m`}), o modelo fica ${fmt(escalaGeometrica)} vezes o tamanho real no mapa. Para o tamanho real, o Scale devia ser ${fmt(unidadeDoProjecto.metros / metrosDoMapa)}. Aplica-se o que está declarado.`,
      )
    }
    const [e, n] = origemEmMetros
    if (Math.abs(e) > 1e8 || Math.abs(n) > 1e8) {
      avisos.push(
        `A origem fica a ${fmt(Math.max(Math.abs(e), Math.abs(n)) / 1000)} km: fora do que um CRS projectado usa. Confirmar a unidade do mapa no ficheiro.`,
      )
    }
  }

  return {
    conversao: { id, classe: d.classe, eastings, northings, altura, xAbscissa, xOrdenada, scale, factores },
    crs,
    unidadeDoProjecto,
    unidadeDoMapa,
    escalaGeometrica,
    origemEmMetros,
    rotacaoGraus: (Math.atan2(xOrdenada, xAbscissa) * 180) / Math.PI,
    alternativas,
    avisos,
  }
}

// ---------------------------------------------------------------------
// IfcSite: latitude, longitude e elevação
// ---------------------------------------------------------------------

export interface LocalizacaoDoSitio {
  id: number
  latitude: number
  longitude: number
  /** RefElevation em metros (pela unidade do projecto), ou null. */
  elevacaoMetros: number | null
  /** TrueNorth do contexto, em graus a partir de +Y no sentido horário, ou null. */
  trueNorthGraus: number | null
  avisos: string[]
  /** Preenchido quando o viewer usou o sítio para pôr o modelo no mapa. */
  aplicado?: { epsg: string; convergenciaGraus: number }
}

/** IfcCompoundPlaneAngleMeasure (graus, minutos, segundos[, milionésimos])
 *  em graus decimais. Todos os componentes têm o mesmo sinal. */
export function anguloComposto(arg: string | undefined): number | null {
  const t = (arg ?? '').trim()
  if (!t.startsWith('(')) return null
  const partes = splitTopLevelArgs(t.slice(1, -1)).map((x) => Number(x.trim()))
  if (partes.length < 3 || partes.slice(0, 3).some((x) => !Number.isFinite(x))) return null
  const [g, m, sec, mi] = [partes[0], partes[1], partes[2], Number.isFinite(partes[3]) ? partes[3] : 0]
  const negativo = [g, m, sec, mi].some((x) => x < 0 || Object.is(x, -0))
  const v = Math.abs(g) + Math.abs(m) / 60 + (Math.abs(sec) + Math.abs(mi) / 1e6) / 3600
  return negativo ? -v : v
}

/** O TrueNorth declarado no IfcGeometricRepresentationContext (graus a
 *  partir de +Y, sentido horário), ou null. */
export function trueNorth(texto: string): number | null {
  for (const m of texto.matchAll(/#(\d+)\s*=\s*IFCGEOMETRICREPRESENTATIONCONTEXT\s*\(/g)) {
    const e = args(texto, parseInt(m[1], 10))
    const dir = e ? lerRef(e.a[5]) : null
    const d = dir !== null ? args(texto, dir) : null
    if (!d || d.classe !== 'IFCDIRECTION') continue
    const v = splitTopLevelArgs((d.a[0] ?? '').replace(/^\(|\)$/g, '')).map((x) => Number(x.trim()))
    if (v.length < 2 || !Number.isFinite(v[0]) || !Number.isFinite(v[1]) || (v[0] === 0 && v[1] === 0)) continue
    return (Math.atan2(v[0], v[1]) * 180) / Math.PI
  }
  return null
}

/** O primeiro IfcSite com latitude e longitude. Latitude e longitude a
 *  zero (o que alguns programas escrevem por omissão) não contam. */
export function lerLocalizacaoDoSitio(texto: string): LocalizacaoDoSitio | null {
  const avisos: string[] = []
  const sitios = [...texto.matchAll(/#(\d+)\s*=\s*IFCSITE\s*\(/g)].map((m) => parseInt(m[1], 10))
  const comCoordenadas: LocalizacaoDoSitio[] = []
  for (const id of sitios) {
    const e = args(texto, id)
    if (!e) continue
    const latitude = anguloComposto(e.a[9])
    const longitude = anguloComposto(e.a[10])
    if (latitude === null || longitude === null) continue
    if (latitude === 0 && longitude === 0) {
      avisos.push(`O IfcSite #${id} tem latitude e longitude a zero: não se usam.`)
      continue
    }
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
      avisos.push(`O IfcSite #${id} tem latitude/longitude fora do domínio (${fmt(latitude)}, ${fmt(longitude)}): não se usam.`)
      continue
    }
    const el = numero(e.a[11])
    const { unidade } = lerUnidadeDoProjecto(texto)
    let elevacaoMetros: number | null = null
    if (el !== null) {
      if (unidade) elevacaoMetros = el * unidade.metros
      else avisos.push(`RefElevation do #${id} sem unidade do projecto conhecida: não se usa.`)
    }
    // A latitude e a longitude são as da origem do sítio. Se o sítio
    // estiver deslocado em relação ao projecto, diz-se.
    const lp = lerRef(e.a[5])
    const lpa = lp !== null ? args(texto, lp) : null
    const a2 = lpa?.classe === 'IFCLOCALPLACEMENT' ? lerRef(lpa.a[1]) : null
    const a2a = a2 !== null ? args(texto, a2) : null
    const pt = a2a ? lerRef(a2a.a[0]) : null
    const pta = pt !== null ? args(texto, pt) : null
    const coords = pta ? splitTopLevelArgs((pta.a[0] ?? '').replace(/^\(|\)$/g, '')).map((x) => Number(x.trim())) : []
    if ((lpa && lpa.a[0] !== '$') || coords.some((c) => Number.isFinite(c) && c !== 0)) {
      avisos.push(`O IfcSite #${id} não está na origem do projecto (posição relativa #${lp}): a latitude e a longitude são as da origem do sítio, e o viewer põe nelas a origem do ficheiro.`)
    }
    comCoordenadas.push({ id, latitude, longitude, elevacaoMetros, trueNorthGraus: trueNorth(texto), avisos })
  }
  if (comCoordenadas.length === 0) return null
  const s0 = comCoordenadas[0]
  if (comCoordenadas.length > 1) {
    s0.avisos.push(`Há ${comCoordenadas.length} IfcSite com latitude/longitude: usa-se o primeiro, #${s0.id}.`)
  }
  s0.avisos.unshift(...avisos.filter((a) => !s0.avisos.includes(a)))
  return s0
}

const sitios = new Map<string, LocalizacaoDoSitio | null>()
export function guardarSitio(modelId: string, s: LocalizacaoDoSitio | null): void {
  sitios.set(modelId, s)
}
export function sitioGuardado(modelId: string): LocalizacaoDoSitio | null | undefined {
  return sitios.get(modelId)
}
